import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { TRANSFORMERS } from "@lexical/markdown";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import ToolbarPlugin from "./plugins/ToolbarPlugin";

import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { $getRoot } from "lexical";
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { ImageNode } from "./nodes/ImageNode";
import LexicalAutoLinkPlugin from "./plugins/AutoLinkPlugin";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import { LinkClickPlugin } from "./plugins/LinkClickPlugin";
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import TableActionMenuPlugin from "./plugins/TableActionMenuPlugin";
import TableCellResizerPlugin from "./plugins/TableCellResizerPlugin";
import TableHoverActionsPlugin from "./plugins/TableHoverActions";
import "./styles.css";
import Theme from "./Theme";

export function rteHasContent(html) {
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return (
    temp.textContent.trim() !== "" ||
    temp.querySelectorAll("img").length > 0 ||
    temp.querySelectorAll("table").length > 0
  );
}

function createEditorStateFromHTML(html) {
  return (editor) => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(html, "text/html");

    const nodes = $generateNodesFromDOM(editor, dom);

    const root = $getRoot();
    root.clear();
    root.append(...nodes);
  };
}

const RichTextEditor = ({
  id = "rich-text-editor",
  label,
  defaultValue,
  onChange,
  readOnly = false,
}) => {
  const [floatingAnchorElem, setFloatingAnchorElem] = useState(null);
  const onRef = (_floatingAnchorElem) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };
  const [inputValue, setInputValue] = useState("");
  const deferredInputValue = useDeferredValue(inputValue);

  useEffect(() => {
    if (!readOnly && typeof onChange === "function") {
      onChange(deferredInputValue);
    }
  }, [deferredInputValue, readOnly]);

  const initialConfig = useMemo(
    () => ({
      editable: !readOnly,
      // The editor initial value
      editorState: defaultValue
        ? createEditorStateFromHTML(defaultValue)
        : undefined,
      // The editor theme
      theme: Theme,
      // Handling of errors during update
      onError(error) {
        throw error;
      },
      // Any custom nodes go here
      nodes: [
        HeadingNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        CodeNode,
        CodeHighlightNode,
        TableNode,
        TableCellNode,
        TableRowNode,
        AutoLinkNode,
        LinkNode,
        ImageNode,
      ],
    }),
    []
  );

  const editorRef = useRef(null);

  return (
    <>
      {label && (
        <span id={id} onClick={() => editorRef.current?.focus()}>
          {label}
        </span>
      )}
      <LexicalComposer initialConfig={initialConfig}>
        <div
          style={{ marginTop: label ? 15 : 0 }}
          className={`editor-container ${readOnly ? "read-only" : ""}`}
        >
          {!readOnly && <ToolbarPlugin />}
          <div className="editor-inner">
            <RichTextPlugin
              contentEditable={
                <div ref={onRef}>
                  <ContentEditable
                    ref={editorRef}
                    aria-labelledby={id}
                    className={`editor-input ${readOnly ? "read-only" : ""}`}
                  />
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <AutoFocusPlugin />
            <CodeHighlightPlugin />
            <ListPlugin />
            <TabIndentationPlugin />
            <LinkPlugin />
            <LinkClickPlugin />
            <LexicalAutoLinkPlugin />
            <ListMaxIndentLevelPlugin maxDepth={7} />
            <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
            <TablePlugin hasHorizontalScroll={true} />
            <TableCellResizerPlugin />
            {floatingAnchorElem && (
              <>
                <TableHoverActionsPlugin anchorElem={floatingAnchorElem} />
                <TableActionMenuPlugin
                  anchorElem={floatingAnchorElem}
                  cellMerge={true}
                />
              </>
            )}
            {onChange && (
              <OnChangePlugin
                onChange={(editorState, editor) => {
                  editorState.read(() => {
                    const html = $generateHtmlFromNodes(editor);
                    setInputValue(html);
                  });
                }}
              />
            )}
          </div>
        </div>
      </LexicalComposer>
    </>
  );
};

export default RichTextEditor;
